import { useEffect, useRef } from 'react'
import { useMeetingRecordings } from '@/hooks/useMeetingRecordings'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingStore } from '@/store/recording.store'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

// Finished notes older than this are history, not news — the first load after
// this ships must not flag a weeks-old recording as "ready to review".
const REVIEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000

/**
 * The user's most recent recording, for the record button: where its notes
 * are in the pipeline and whether finished notes are still waiting to be
 * reviewed. Announces notes that finish while the app is open, and counts
 * them as reviewed once the note itself is open (from anywhere).
 */
export function useLatestRecording({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const orgSlug = useOrganizationStore(s => s.currentOrganization?.slug)
  const reviewedIds = useRecordingStore(s => s.reviewedRecordingIds)
  const markRecordingReviewed = useRecordingStore(s => s.markRecordingReviewed)
  const dismissedKey = useRecordingStore(s => s.dismissedRecordingKey)
  const dismissRecording = useRecordingStore(s => s.dismissRecording)
  const { recordings, retryRecording } = useMeetingRecordings({
    limit: 1,
    enabled,
  })
  const recording = enabled ? recordings[0] : undefined
  const noteId = recording?.status === 'READY' ? recording.noteId : null

  const isReadyToReview =
    !!recording &&
    !!noteId &&
    !reviewedIds.includes(recording.id) &&
    Date.now() - new Date(recording.updatedAt).getTime() < REVIEW_WINDOW_MS

  const isDismissed =
    !!recording && dismissedKey === `${recording.id}:${recording.status}`

  // Out of the menu; finished notes also stop being flagged as unread.
  const dismiss = () => {
    if (!recording) return
    dismissRecording(recording.id, recording.status)
    if (recording.status === 'READY') markRecordingReviewed(recording.id)
  }

  const openNote = () => {
    if (orgSlug && noteId) navigate(`/${orgSlug}/notes/${noteId}`)
  }

  // Viewing the note (here, a notification, the Recordings tab…) reviews it.
  useEffect(() => {
    if (recording && noteId && pathname.endsWith(`/notes/${noteId}`)) {
      markRecordingReviewed(recording.id)
    }
  }, [recording, noteId, pathname, markRecordingReviewed])

  // Only a live transition into READY is news; a list that loads already
  // READY was finished before this session saw it processing.
  const previousRef = useRef<{ id: string; status: string } | null>(null)
  useEffect(() => {
    if (!recording) return
    const previous = previousRef.current
    previousRef.current = { id: recording.id, status: recording.status }
    if (
      previous?.id === recording.id &&
      previous.status !== 'READY' &&
      recording.status === 'READY' &&
      recording.noteId &&
      orgSlug &&
      !pathname.endsWith(`/notes/${recording.noteId}`)
    ) {
      const notePath = `/${orgSlug}/notes/${recording.noteId}`
      toast.success('Your meeting notes are ready', {
        description: recording.title ?? undefined,
        action: { label: 'Review', onClick: () => navigate(notePath) },
      })
    }
  }, [recording, pathname, orgSlug, navigate])

  return {
    recording: isDismissed ? undefined : recording,
    isReadyToReview,
    openNote,
    retryRecording,
    dismiss,
  }
}
